import FootballState from "../../../state/football-state";
import DownType from "../../../enums/down-type";
import Rule from "../../../../common/rule";
import {
    getPenaltyTeamIds,
    isDefensivePenalty,
    isPenaltyRestoringPossession,
    isPlayNegated
} from "../../../utils/context-utils";

export default class PenaltyAccepted extends Rule {
    // eslint-disable-next-line no-unused-vars
    applicable(
        gameContext,
        ruleContext,
        eventConfiguration,
        { play, fragment }
    ) {
        return [
            FootballState.PenaltyAccepted,
            FootballState.PlayPenaltyPenaltyAccepted,
            FootballState.KickOffPenaltyAccepted
        ].some((s) => s === fragment.associatedState);
    }

    // eslint-disable-next-line no-unused-vars
    async apply(
        gameContext,
        ruleContext,
        eventConfiguration,
        { play, fragment }
    ) {
        const {
            homeTeamHasPossession,
            driveContext,
            currentDriveId,
            fieldPosition,
            firstDownAt,
            down,
            triggerPAT
        } = play.contextSnapshot;

        const penaltyFragment = play.getPreceedingFragment(fragment, [
            FootballState.Penalty,
            FootballState.PlayPenalty,
            FootballState.KickOffPenalty
        ]);

        const causedByDefense = isDefensivePenalty(play, penaltyFragment);
        const { offenseTeamId, defenseTeamId } = getPenaltyTeamIds(play);

        if (ruleContext.isKickoffPenalty) {
            gameContext.setPossessionForTeamId(defenseTeamId);
            ruleContext.setAwardFirstDown(true);
            gameContext.triggerKO = false;
            gameContext.setDriveContext({ triggerDrive: true });
            gameContext.setFieldPosition(fragment.fieldPosition);
            return;
        }

        // A negated kick play is not a voluntary change of possession: the kick
        // is wiped out, so the kicking team keeps the ball and its drive
        // instead of the receiving team taking over.
        const isVoluntaryKickPlay =
            !isPlayNegated(play) &&
            (play.historyContains(FootballState.KickOff) ||
                play.historyContains(FootballState.Punt) ||
                play.historyContains(FootballState.CflKick) ||
                play.historyContains(FootballState.FieldGoalMissed));

        if (fragment.downType.code === DownType.AutomaticFirstDown.code) {
            if (isVoluntaryKickPlay) {
                ruleContext.setAwardFirstDown(true);
                gameContext.setDriveContext({ triggerDrive: true });
            } else {
                gameContext.setPossessionForTeamId(offenseTeamId);
                ruleContext.setAwardFirstDown(true);
                gameContext.setDriveContext(driveContext);
            }
        } else if (fragment.downType.code === DownType.Normal.code) {
            const fieldGoalMissed = play.historyContains(
                FootballState.FieldGoalMissed
            );
            const fieldGoalGood = play.historyContains(
                FootballState.FieldGoalGood
            );

            const shouldRestorePossession = isPenaltyRestoringPossession(
                play,
                penaltyFragment,
                gameContext
            );

            if (shouldRestorePossession) {
                gameContext.setPossessionForTeamId(offenseTeamId);
                gameContext.setFieldPosition(fieldPosition);
                gameContext.setFirstDownAt(firstDownAt);
                gameContext.down = down;
            }

            const yardsGained =
                (fragment.fieldPosition - gameContext.fieldPosition) *
                gameContext.getFieldDirectionMultiplier();

            const shouldAdvanceDown =
                yardsGained < gameContext.yardsToFirstDown();

            if (shouldAdvanceDown) {
                ruleContext.advanceDown = true;
            }

            const negateDownAdvancement =
                yardsGained < 0 ||
                (!fieldGoalMissed && yardsGained === 0) ||
                causedByDefense;

            if (negateDownAdvancement) {
                ruleContext.advanceDown = false;
            }

            const shouldRestoreDriveContext =
                homeTeamHasPossession === gameContext.homeTeamHasPossession &&
                !isVoluntaryKickPlay;

            const shouldAwardFirstDown =
                !shouldRestoreDriveContext ||
                (causedByDefense &&
                    (yardsGained >= gameContext.yardsToFirstDown() ||
                        fieldGoalGood));

            if (shouldRestoreDriveContext) {
                gameContext.currentDriveId = currentDriveId;
                gameContext.setDriveContext(driveContext);
            }

            ruleContext.setAwardFirstDown(shouldAwardFirstDown);

            // TODO: Put this into a separate rule
            if (triggerPAT) {
                gameContext.triggerPAT = true;
                ruleContext.setResetField(false);
                gameContext.setDriveContext({ ...driveContext });
                gameContext.setPossessionForTeamId(offenseTeamId);
            }
        }

        gameContext.setFieldPosition(fragment.fieldPosition);
    }
}
